// 'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Upload,
  CheckCircle,
  User,
  Mail,
  Phone,
  FileText,
  Send,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../components/ui//input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui//label';

export default function ApplyJobForm({ onClose, jobTitle }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    files: [],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [fileNames, setFileNames] = useState([]);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    // Disable background scroll when modal is open
    document.body.style.overflow = 'hidden';

    // Re-enable when modal closes or component unmounts
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData({
      ...formData,
      files: files,
    });
    setFileNames(files.map((file) => file.name));
  };

  // Helper function — convert file to base64
  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  // const uploadFileToDisk = async (file) => {
  //   const url =
  //     'https://xspace.bitrix24.com/rest/790/3pg78swcm1czjwr2/disk.folder.uploadfile.json';
  //   const formData = new FormData();
  //   formData.append('id', 63944); // Folder ID, 0 for root
  //   formData.append('file', file, file.name);
  //   const res = await fetch(url, { method: 'POST', body: formData });
  //   const data = await res.json(); // parse JSON
  //   console.log('Uploaded file response:', data);

  //   if (data?.error) throw new Error(data.error_description);
  //   return data.result.ID; // This is the file ID to attach to SPA
  // };

  const uploadFileToDisk = async (file) => {
    const url =
      'https://xspace.bitrix24.com/rest/790/3pg78swcm1czjwr2/disk.folder.uploadfile.json';

    const formData = new FormData();
    formData.append('id', 63944);
    formData.append('file', file, file.name);

    const res = await fetch(url, { method: 'POST', body: formData });
    const data = await res.json();

    if (data?.error) throw new Error(data.error_description);
    return data.result.ID; // Use this ID in SPA file field
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const { firstName, lastName, email, phone, message, files } = formData;

    // Step 1️⃣ — Get current date in DD.MM.YYYY format
    const submissionDate = new Date();
    const datePart = submissionDate
      .toLocaleDateString('en-GB')
      .replace(/\//g, '.'); // "22.10.2025"

    // Step 2️⃣ — Get current time in HH:MM:SS format
    const timePart = submissionDate
      .toTimeString() // "14:35:12 GMT+0400 (Gulf Standard Time)"
      .split(' ')[0]; // take only "HH:MM:SS"

    // Step 3️⃣ — Combine date and time
    const dateTime = `${datePart} ${timePart}`;

    let bitrixFiles = [];
    if (files && files.length > 0) {
      const promises = files.map(async (file) => {
        const base64 = await toBase64(file);
        return [file.name, base64.split(',')[1]];
        // return {
        //   fileData: [file.name, base64.split(',')[1]],
        // };
      });

      bitrixFiles = await Promise.all(promises);
    }

    // 🧩 Step 3 — Build CRM data object
    const crmFormData = {
      TITLE: `Apply for ${jobTitle}`,
      ufCrm10_1694955334509: firstName,
      ufCrm10_1694955341396: lastName,
      ufCrm10_1694955360500: email,
      ufCrm10_1694955354868: phone,
      ASSIGNED_BY_ID: 790,
      OBSERVER: [{ VALUE: 790 }],
      ufCrm16_1743064314536: dateTime, // Form submission date
      ufCrm16_1761036823: jobTitle || 'General Application',
      ufCrm16_1745500806428: message,
      ufCrm10_1694955379844: '',
    };

    // Attach files only if uploaded
    if (bitrixFiles.length > 0) {
      crmFormData.ufCrm10_1694955379844 = bitrixFiles[0];
    }

    if (!formData.files.length) {
      setError('Please upload your Resume or CV before submitting.');
      setIsSubmitting(false);
      return;
    }

    try {
      // Simulate a delay for demonstration purposes
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const payload = {
        entityTypeId: 160,
        fields: crmFormData,
      };

      console.log(payload, 'payload');

      // 📨 Step 4 — Send request to Bitrix24
      const response = await fetch(
        'https://xspace.bitrix24.com/rest/790/55x03yp3pqn9x1vi/crm.item.add.json',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();
      console.log('Bitrix response:', data);

      if (data.error) {
        throw new Error(
          data.error_description || 'Error creating record in Bitrix'
        );
      }

      // ✅ Success
      setSuccess(true);
      setFeedback(
        'Thank you! Your job application has been submitted successfully. Our admin team will contact you as soon as possible.'
      );

      // Reset form data
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: '',
        files: [],
      });

      // Close the form modal immediately
      // onClose();
    } catch (error) {
      console.error('❌ Error submitting to Bitrix:', error);
      setFeedback(
        'An error occurred while submitting. Please try again later.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* 💼 Job Application Modal */}
      <AnimatePresence>
        <div className='fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden p-4 md:p-6'>
          <div
            className='fixed inset-0 bg-black/70 backdrop-blur-sm'
            onClick={onClose}
          ></div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className='relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-800 rounded-3xl shadow-2xl'
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className='absolute top-4 right-4 z-10 p-2 rounded-full bg-white/90 dark:bg-gray-700/90 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors shadow-md'
              aria-label='Close'
            >
              <X className='w-5 h-5 text-gray-700 dark:text-gray-300' />
            </button>

            {/* Header */}
            <div className='bg-gradient-to-r from-[#fe2a53] to-[#38f5ef] p-6 md:p-8'>
              <h2 className='text-2xl md:text-3xl font-bold text-white mb-2 flex flex-wrap items-center'>
                {jobTitle ? `Apply for ${jobTitle}` : 'Apply for a job'}
              </h2>
              <p className='mt-2 text-white/90 text-sm md:text-base'>
                Join our team at X-Space Real Estate and take your career to the
                next level
              </p>
            </div>

            <form onSubmit={handleSubmit} className='p-6 md:p-8'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6'>
                <div className='space-y-2'>
                  <Label
                    htmlFor='firstName'
                    className='text-gray-700 dark:text-gray-300'
                  >
                    First Name
                  </Label>
                  <div className='relative'>
                    <div className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400'>
                      <User className='h-5 w-5' />
                    </div>
                    <Input
                      id='firstName'
                      name='firstName'
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder='John'
                      className='pl-10 py-6 bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-[16px]'
                      required
                    />
                  </div>
                </div>
                <div className='space-y-2'>
                  <Label
                    htmlFor='lastName'
                    className='text-gray-700 dark:text-gray-300'
                  >
                    Last Name
                  </Label>
                  <div className='relative'>
                    <div className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400'>
                      <User className='h-5 w-5' />
                    </div>
                    <Input
                      id='lastName'
                      name='lastName'
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder='Doe'
                      className='pl-10 py-6 bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-[16px]'
                      required
                    />
                  </div>
                </div>
                <div className='space-y-2'>
                  <Label
                    htmlFor='email'
                    className='text-gray-700 dark:text-gray-300'
                  >
                    Email
                  </Label>
                  <div className='relative'>
                    <div className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400'>
                      <Mail className='h-5 w-5' />
                    </div>
                    <Input
                      id='email'
                      name='email'
                      type='email'
                      value={formData.email}
                      onChange={handleChange}
                      placeholder='john@example.com'
                      className='pl-10 py-6 bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-[16px]'
                      required
                    />
                  </div>
                </div>
                <div className='space-y-2'>
                  <Label
                    htmlFor='phone'
                    className='text-gray-700 dark:text-gray-300'
                  >
                    Phone
                  </Label>
                  <div className='relative'>
                    <div className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400'>
                      <Phone className='h-5 w-5' />
                    </div>
                    <Input
                      id='phone'
                      name='phone'
                      type='tel'
                      value={formData.phone}
                      onKeyDown={(e) => {
                        const allowedKeys = [
                          'Backspace',
                          'Delete',
                          'ArrowLeft',
                          'ArrowRight',
                          'Tab',
                          '-', // allow dash
                        ];

                        // Allow Ctrl/Cmd + shortcuts (Ctrl+A, Ctrl+C, Ctrl+V, etc.)
                        if (e.ctrlKey || e.metaKey) return;

                        // Allow numbers and other allowed keys
                        if (
                          !/[0-9]/.test(e.key) &&
                          !allowedKeys.includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                      onChange={handleChange} // still needed to update state
                      placeholder='+971-50-123-4567'
                      className='pl-10 py-6 bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-[16px]'
                      required
                    />
                  </div>
                </div>
              </div>

              <div className='space-y-2 mb-6'>
                <Label
                  htmlFor='message'
                  className='text-gray-700 dark:text-gray-300'
                >
                  Cover Letter / Message
                </Label>
                <div className='relative'>
                  <div className='absolute left-3 top-3 text-gray-400'>
                    <FileText className='h-5 w-5' />
                  </div>
                  <Textarea
                    id='message'
                    name='message'
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about yourself and why you're interested in this position..."
                    className='pl-10 pt-2 bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 min-h-[120px] text-[16px]'
                    rows={4}
                    required
                  />
                </div>
              </div>

              <div className='space-y-2 mb-8'>
                <Label
                  htmlFor='resume'
                  className='text-gray-700 dark:text-gray-300'
                >
                  Resume / CV
                </Label>
                <div className='border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors bg-gray-50 dark:bg-gray-900'>
                  <input
                    id='resume'
                    type='file'
                    multiple
                    onChange={handleFileChange}
                    className='hidden'
                    accept='.pdf,.doc,.docx'
                  />
                  <label
                    htmlFor='resume'
                    className='cursor-pointer flex flex-col items-center'
                  >
                    <Upload className='w-10 h-10 text-gray-400 mb-3' />
                    <span className='text-sm md:text-base font-medium text-gray-700 dark:text-gray-300'>
                      Click to upload your resume and other documents
                    </span>
                    <span className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
                      PDF, DOC, DOCX up to 10MB
                    </span>
                  </label>
                </div>
                {fileNames.length > 0 && (
                  <div className='mt-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg'>
                    <p className='text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                      Selected files:
                    </p>
                    <ul className='text-xs text-gray-600 dark:text-gray-400 space-y-1'>
                      {fileNames.map((name, index) => (
                        <li key={index} className='flex items-center'>
                          <CheckCircle className='w-3 h-3 text-green-500 mr-1 flex-shrink-0' />
                          <span className='truncate'>{name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {error && (
                <div className='mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm'>
                  {error}
                </div>
              )}

              <div className='flex justify-end'>
                <Button
                  type='submit'
                  disabled={isSubmitting}
                  className='bg-gradient-to-r bg-primarycolor dark:bg-secondarycolor hover:bg-secondarycolor dark:hover:bg-primarycolor text-white hover:text-black dark:hover:text-white   px-8 py-6 rounded-full flex items-center gap-2 shadow-lg'
                >
                  {isSubmitting ? (
                    <>
                      <div className='animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2'></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className='h-5 w-5' />
                      Submit Application
                    </>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      </AnimatePresence>

      {/* 🎉 Success Overlay */}
      {success && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className='fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4'
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 100, damping: 15 }}
            className='relative max-w-lg w-full bg-white dark:bg-gray-900 rounded-3xl p-10 text-center shadow-2xl'
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1, rotate: 360 }}
              transition={{ type: 'spring', damping: 12 }}
              className='w-28 h-28 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center mb-6 mx-auto shadow-inner ring-4 ring-green-400/20'
            >
              <CheckCircle className='w-14 h-14 text-green-600 dark:text-green-400 drop-shadow-lg' />
            </motion.div>

            <h3 className='text-3xl font-extrabold bg-gradient-to-r from-green-600 to-teal-500 bg-clip-text text-transparent mb-3'>
              Application Submitted!
            </h3>

            <p className='text-gray-700 dark:text-gray-300 text-base md:text-lg mb-8 leading-relaxed'>
              Thank you! Your job application has been submitted successfully.
              Our admin team will contact you as soon as possible.
            </p>

            <motion.button
              onClick={onClose}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className='px-8 py-3 bg-gradient-to-r from-green-600 to-teal-500 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all'
            >
              Close
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </>
  );
}
